import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Skeleton,
  Container,
  Collapse,
  IconButton
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Groups = () => {
  const { accessToken, isAdmin, isSuperuser } = useAuth();
  const navigate = useNavigate();
  const [allGroups, setAllGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sites, setSites] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGroupsOpen, setIsGroupsOpen] = useState(true);
  const [filters, setFilters] = useState({
    name: '',
    site: '',
    site_name: ''
  });

  const baseURL = process.env.REACT_APP_BASEURL;

  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch groups and sites on mount or when accessToken changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch groups
        const groupsResponse = await axios.get(`${baseURL}/groups`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { name: filters.name || null, site: filters.site || null }
        });
        const fetchedGroups = groupsResponse.data || [];
        setAllGroups(fetchedGroups);
        setGroups(fetchedGroups);

        // Fetch sites
        const sitesResponse = await axios.get(`${baseURL}/get-sites`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const fetchedSites = sitesResponse.data.data || [];
        const siteMap = new Map(fetchedSites.map(site => [String(site.id), site.name || `Site ${site.id}`]));
        setSites(siteMap);
      } catch (error) {
        console.error('Data fetch error:', error.response ? error.response.data : error.message);
        setError('Failed to load groups or sites. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accessToken]);

  // Set initial filter values after sites is fetched
  useEffect(() => {
    if (sites.size > 0 && filters.site_name === '') {
      const sortedSites = [...sites.entries()].sort((a, b) => {
        const nameA = a[1] || `Site ${a[0]}`;
        const nameB = b[1] || `Site ${b[0]}`;
        return nameA.localeCompare(nameB) || Number(a[0]) - Number(b[0]);
      });
      const [firstSiteId, firstSiteName] = sortedSites[0];
      setFilters({
        name: '',
        site: firstSiteId,
        site_name: firstSiteName
      });
    }
  }, [sites]);

  // Filter groups locally whenever filters change
  useEffect(() => {
    const filteredGroups = allGroups.filter(group => {
      const nameMatch = filters.name
        ? group.name?.toLowerCase().includes(filters.name.toLowerCase())
        : true;
      const siteMatch = filters.site
        ? String(group.site) === String(filters.site)
        : true;
      const siteNameMatch = filters.site_name
        ? (sites.get(String(group.site)) || `Site ${group.site}`)
            .toLowerCase()
            .includes(filters.site_name.toLowerCase())
        : true;

      return nameMatch && siteMatch && siteNameMatch;
    });

    setGroups(filteredGroups);
  }, [filters, allGroups, sites]);

  // Handle filter changes and synchronize Site ID and Site Name
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => {
      const newFilters = { ...prev, [name]: value };

      if (name === 'site' && value) {
        const siteName = sites.get(String(value)) || `Site ${value}`;
        newFilters.site_name = siteName;
      } else if (name === 'site' && !value) {
        newFilters.site_name = '';
      } else if (name === 'site_name' && value) {
        const siteEntry = [...sites.entries()].find(
          ([id, name]) => name === value
        );
        newFilters.site = siteEntry ? String(siteEntry[0]) : '';
      } else if (name === 'site_name' && !value) {
        newFilters.site = '';
      }

      return newFilters;
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
  };

  const handleClearFilters = () => {
    setFilters({
      name: '',
      site: '',
      site_name: ''
    });
  };

  const handleViewStatistics = (groupId, siteId) => {
    navigate('/dashboard/statistics', { state: { groupId, siteId } });
  };

  const getSiteDisplay = (siteId) => {
    const siteName = sites.get(String(siteId));
    return siteName ? `${siteName} (${siteId})` : String(siteId);
  };

  const LoadingSkeleton = () => (
    <Container maxWidth="xl" disableGutters sx={{ p: 0 }}>
      <Box sx={{ p: 0 }}>
        <Typography variant="h4" gutterBottom>
          <Skeleton width="30%" height={40} />
        </Typography>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            {Array(3).fill().map((_, index) => (
              <Skeleton key={index} variant="rectangular" width={isXsScreen ? '100%' : 150} height={56} />
            ))}
          </Box>
          <Skeleton variant="rectangular" width={isXsScreen ? '100%' : 120} height={40} />
        </Box>
        <Paper sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography variant="h6" gutterBottom>
            <Skeleton width="20%" height={32} />
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 300, sm: 800 } }}>
              <TableHead>
                <TableRow>
                  {['ID', 'Name', 'Site', 'Devices', 'Last Measurement', 'Protocol Versions'].map((header) => (
                    <TableCell key={header}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array(3).fill().map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {Array(6).fill().map((_, cellIndex) => (
                      <TableCell key={cellIndex}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>
    </Container>
  );

  if (loading) return <LoadingSkeleton />;

  if (error) return (
    <Container maxWidth="xl">
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <Typography color="error">{error}</Typography>
      </Box>
    </Container>
  );

  const sortedSiteEntries = [...sites.entries()].sort((a, b) => {
    const nameA = a[1] || `Site ${a[0]}`;
    const nameB = b[1] || `Site ${b[0]}`;
    return nameA.localeCompare(nameB) || Number(a[0]) - Number(b[0]);
  });

  return (
    <Container maxWidth="xl" disableGutters sx={{ p: 0 }}>
      <Box sx={{ p: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Groups
        </Typography>
        {(isAdmin || isSuperuser) && (
          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/groups/add")}
              sx={{
                fontSize: { xs: "0.9rem", md: "1rem" },
                textTransform: "none",
              }}
            >
              Add Group
            </Button>
          </Box>
        )}
      </Box>
        

        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
            Filter Groups
          </Typography>
          <Box
            component="form"
            onSubmit={handleFilterSubmit}
            sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}
          >
            <TextField
              label="Group Name"
              name="name"
              value={filters.name}
              onChange={handleFilterChange}
              variant="outlined"
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 150 }, '& .MuiInputLabel-root': { color: 'black', fontWeight: 100 } }}
            />
            <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel id="site-name-label" sx={{ color: 'black', fontWeight: 100 }}>Site Name</InputLabel>
              <Select
                labelId="site-name-label"
                name="site_name"
                value={filters.site_name}
                onChange={handleFilterChange}
                label="Site Name"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {sortedSiteEntries.map(([id, name]) => (
                  <MenuItem key={id} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel id="site-id-label" sx={{ color: 'black', fontWeight: 100 }}>Site ID</InputLabel>
              <Select
                labelId="site-id-label"
                name="site"
                value={filters.site}
                onChange={handleFilterChange}
                label="Site ID"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {sortedSiteEntries.map(([id, name]) => (
                  <MenuItem key={id} value={id}>{id}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" color="secondary" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 1, sm: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              p: 1,
              borderRadius: 1
            }}
            onClick={() => setIsGroupsOpen(!isGroupsOpen)}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' }, flexGrow: 1 }}
            >
              Groups
            </Typography>
            <IconButton size="small">
              {isGroupsOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={isGroupsOpen}>
            {groups.length > 0 ? (
              <Box sx={{ overflowX: 'auto', mt: 2 }}>
                <Table sx={{ minWidth: { xs: 300, sm: 800 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Site (ID)</TableCell>
                      <TableCell>Devices</TableCell>
                      <TableCell>Last Measurement</TableCell>
                      <TableCell>Protocol Versions</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.map((group) => (
                      <TableRow 
                        key={group.id}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'grey.100', outline: '2px solid', outlineColor: theme.palette.primary.main },
                        }}
                      >
                        <TableCell>{group.id}</TableCell>
                        <TableCell>{group.name}</TableCell>
                        <TableCell>{getSiteDisplay(group.site)}</TableCell>
                        <TableCell>{group.devices.length > 0 ? group.devices.join(', ') : 'None'}</TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          {group.last_measurement ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                Timestamp: {new Date(group.last_measurement.timestamp).toLocaleString()}
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                Moisture: {(group.last_measurement.moisture * 100).toFixed(1)}%
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                Temperature: {group.last_measurement.temperature.toFixed(1)}°C
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                Salinity: {group.last_measurement.salinity}
                              </Typography>
                            </Box>
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell>{group.devices_protocol_versions.length > 0 ? group.devices_protocol_versions.join(', ') : 'None'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: "1rem" }}>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={() => navigate(`/groups/edit-group/${group.id}`)}
                              sx={{
                                fontSize: { xs: "0.9rem", md: "1rem" },
                                textTransform: "none",
                                color: "white"
                              }}
                            >
                              Edit
                          </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleViewStatistics(group.id, group.site)}
                              sx={{
                                fontSize: { xs: "0.9rem", md: "1rem" },
                                textTransform: "none",
                                color: "white"
                              }}
                            >
                              View Statistics
                          </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mt: 2 }}>
                No groups found matching the filters.
              </Typography>
            )}
          </Collapse>
        </Paper>
      </Box>
    </Container>
  );
};

export default Groups;